/**
 * cloud_shadows — the ONE cloud-shadow texture + config every Atlas surface
 * shares (shader wave stage 4): the engine cloud module's weather map (R =
 * coverage, the field the raymarch samples) addressed with the same lattice
 * tilt and 10 km repeat, so the shadow on the ground lies under the cloud
 * overhead; when the clouds are off (potato) or not built yet, the engine's
 * baked tileable coverage stands in. Handles land in `cloudShadowHandles`
 * for AtlasShaderFxModule's per-frame drift/strength drive.
 */
import type * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { applyCloudShadowLayer, generateCloudCoverageTexture } from 'enginex/render/materials/CloudShadowLayer.js';
import { CLOUD_FIELD_ROTATION, CLOUD_FIELD_SCALE_M, cloudShadowHandles, stage4Stats } from './stage4_fx.js';
import { activePreset } from './quality_tiers.js';

let baked: THREE.Texture | null = null;

export function cloudShadowTexture(app: App | null): THREE.Texture {
  const clouds = app?.getModule('Clouds') as { weatherMap?: THREE.Texture | null } | undefined;
  const wm = clouds?.weatherMap ?? null;
  if (wm) { stage4Stats.cloudSource = 'clouds'; return wm; }
  if (!baked) baked = generateCloudCoverageTexture(256, 7);
  stage4Stats.cloudSource = 'baked';
  return baked;
}

/** Apply the shared cloud-shadow layer to a material (idempotent per material). */
export function applyCloudShadows(app: App | null, material: THREE.Material & { colorNode?: unknown; userData: Record<string, unknown> }): boolean {
  if (material.userData.cloudShadow) return false;
  try {
    // Audit 16.1 (2026-09-15): into the LIGHT, not the albedo — a lamp under a
    // cloud keeps shining and metals keep their F0. Needs the sun's shadow to
    // run at all (`receivedShadowNode` fires per shadow-casting light) and a
    // NODE material (three's classic→node conversion does not carry that slot),
    // else the albedo path stands.
    const isNode = !!(material as { isNodeMaterial?: boolean }).isNodeMaterial;
    const mode: 'light' | 'albedo' = isNode && activePreset().shadowsEnabled ? 'light' : 'albedo';
    const h = applyCloudShadowLayer(material, { texture: cloudShadowTexture(app), worldScale: CLOUD_FIELD_SCALE_M, rotation: CLOUD_FIELD_ROTATION, strength: stage4Stats.cloudShadowStrength, threshold: 0.4, softness: 0.3, mode });
    if (mode === 'light') stage4Stats.cloudShadowInLight = (stage4Stats.cloudShadowInLight ?? 0) + 1;
    cloudShadowHandles.add(h);
    material.userData.cloudShadow = true;
    stage4Stats.cloudShadowMats++;
    return true;
  } catch { return false; }
}
